/**
 * Stale-while-revalidate cache for API data.
 *
 * The idea: the ~700-800ms it takes to reach the server across the
 * Nairobi–Amsterdam distance is unavoidable — but the user should never
 * have to WATCH that wait on every visit. So we show whatever we last
 * fetched INSTANTLY (even if it's a few minutes old), while quietly
 * fetching the fresh version underneath. If anything changed, the screen
 * updates a moment later — but the app never opens to a blank spinner
 * when we already know roughly what it should show.
 *
 * Stored in localStorage so it survives refreshes and app restarts.
 */

const PREFIX = 'mm_cache:';

export const getCached = (key) => {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return null;
    const { data, savedAt } = JSON.parse(raw);
    return { data, savedAt, ageMs: Date.now() - savedAt };
  } catch (e) {
    return null;
  }
};

export const setCached = (key, data) => {
  try {
    localStorage.setItem(PREFIX + key, JSON.stringify({ data, savedAt: Date.now() }));
  } catch (e) {
    // Storage full or unavailable — fail silently, caching is a nice-to-have
  }
};

export const clearCached = (key) => {
  try {
    localStorage.removeItem(PREFIX + key);
  } catch (e) { /* ignore */ }
};

/**
 * The core pattern: call `fetcher` for fresh data, but resolve INSTANTLY
 * with cached data if we have any — then call `onFresh` when the real
 * network response lands, however long that takes.
 *
 * Usage:
 *   useStaleWhileRevalidate('people', () => axios.get(...).then(r=>r.data), {
 *     onCached: (data) => setPeople(data),   // instant, from last visit
 *     onFresh:  (data) => setPeople(data),   // updates once network resolves
 *     onError:  (err)  => setError(...),     // only if there's NO cache to fall back on
 *   });
 */
export async function staleWhileRevalidate(key, fetcher, { onCached, onFresh, onError } = {}) {
  const cached = getCached(key);
  if (cached && onCached) onCached(cached.data, cached.ageMs);

  try {
    const fresh = await fetcher();
    setCached(key, fresh);
    if (onFresh) onFresh(fresh);
    return fresh;
  } catch (err) {
    if (!cached && onError) onError(err);
    // If we have cache, a failed background refresh is not worth alarming
    // the user about — they're already looking at (slightly stale) data.
    throw err;
  }
}
